/**
 * Settings > Inspection types: the organisation's own inspection types.
 * Admins add, rename, describe, reorder and deactivate them; a deactivated
 * type leaves the pickers but keeps its inspections. The route and the menu
 * entry sit behind the `inspections` flag; changes need the type entity's
 * create / edit permissions, which the API enforces too.
 */

import { useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, Pencil, Plus } from 'lucide-react'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { useFormTemplates } from '@/admin/hooks/use-forms'
import {
  type InspectionSubType,
  useInspectionTypeSettings,
  useReorderInspectionTypes,
  useSaveInspectionType,
} from '@/admin/hooks/use-inspections'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { toastApiError } from '@/shared/lib/api-error'
import { InspectionTypeDialog } from './inspection-type-dialog'

export function InspectionTypesTab() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canCreate = can('inspection_subtype.create')
  const canEdit = can('inspection_subtype.edit')
  const { data: types = [], isLoading, isFetching } = useInspectionTypeSettings()
  const { data: forms = [] } = useFormTemplates()
  const formNames = new Map(forms.map((form) => [form.key, form.name]))
  const save = useSaveInspectionType()
  const reorder = useReorderInspectionTypes()
  // `type: null` adds a new type; a row edits that type.
  const [dialog, setDialog] = useState<{ type: InspectionSubType | null } | null>(
    null,
  )

  function move(index: number, offset: number) {
    const uuids = types.map((type) => type.uuid)
    ;[uuids[index], uuids[index + offset]] = [uuids[index + offset], uuids[index]]
    reorder.mutate(uuids, {
      onError: (err) =>
        toastApiError(
          err,
          t('inspectionTypes.reorderFailed', 'Could not reorder the inspection types.'),
        ),
    })
  }

  function setActive(type: InspectionSubType, isActive: boolean) {
    save.mutate(
      { uuid: type.uuid, is_active: isActive },
      {
        onError: (err) =>
          toastApiError(
            err,
            t('inspectionTypes.saveFailed', 'Could not save the inspection type.'),
          ),
      },
    )
  }

  const columns: DataTableColumn<InspectionSubType>[] = [
    {
      id: 'name',
      header: t('inspectionTypes.colName', 'Name'),
      cell: (row) => (
        <div className="flex max-w-[420px] flex-col whitespace-normal">
          <span className="text-[13px] font-medium text-[var(--ink)]">{row.label}</span>
          {row.description && (
            <span className="text-[12px] text-[var(--ink-3)]">{row.description}</span>
          )}
        </div>
      ),
    },
    {
      id: 'links',
      header: t('inspectionTypes.colLinks', 'Must be linked to'),
      cell: (row) =>
        row.requires_claim || row.requires_holding ? (
          <div className="flex flex-wrap gap-1">
            {row.requires_claim && (
              <Badge variant="secondary">{t('inspectionTypes.linkClaim', '{{claimNoun}}')}</Badge>
            )}
            {row.requires_holding && (
              <Badge variant="secondary">
                {t('inspectionTypes.linkRegistration', '{{holdingNoun}}')}
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-[12px] text-[var(--ink-3)]">-</span>
        ),
    },
    {
      id: 'checklist',
      header: t('inspectionTypes.colChecklist', 'Checklist'),
      cell: (row) => {
        const key = row.configuration_data?.checklist_template
        return typeof key === 'string' && key ? (
          <span className="text-[12px] text-[var(--ink-2)]">{formNames.get(key) ?? key}</span>
        ) : (
          <span className="text-[12px] text-[var(--ink-3)]">-</span>
        )
      },
    },
    {
      id: 'status',
      header: t('inspectionTypes.colStatus', 'Status'),
      cell: (row) =>
        row.is_active ? (
          <Badge
            variant="outline"
            className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
          >
            {t('inspectionTypes.active', 'Active')}
          </Badge>
        ) : (
          <Badge variant="outline">{t('inspectionTypes.inactive', 'Inactive')}</Badge>
        ),
    },
  ]
  if (canEdit) {
    columns.push({
      id: 'actions',
      header: '',
      cell: (row) => {
        const index = types.findIndex((type) => type.uuid === row.uuid)
        // Each control stops the click reaching the row, which opens the editor.
        const act = (run: () => void) => (e: MouseEvent) => {
          e.stopPropagation()
          run()
        }
        return (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="icon-xs"
              disabled={index === 0 || reorder.isPending}
              onClick={act(() => move(index, -1))}
              aria-label={t('inspectionTypes.moveUp', 'Move up')}
              title={t('inspectionTypes.moveUp', 'Move up')}
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              disabled={index === types.length - 1 || reorder.isPending}
              onClick={act(() => move(index, 1))}
              aria-label={t('inspectionTypes.moveDown', 'Move down')}
              title={t('inspectionTypes.moveDown', 'Move down')}
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={act(() => setDialog({ type: row }))}
              aria-label={t('inspectionTypes.edit', 'Edit')}
              title={t('inspectionTypes.edit', 'Edit')}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              disabled={save.isPending}
              onClick={act(() => setActive(row, !row.is_active))}
            >
              {row.is_active
                ? t('inspectionTypes.deactivate', 'Deactivate')
                : t('inspectionTypes.activate', 'Activate')}
            </Button>
          </div>
        )
      },
    })
  }

  return (
    <div className="mt-8">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-[15px] font-medium text-[var(--ink)]">
            {t('inspectionTypes.title', 'Inspection types')}
          </h2>
          <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">
            {t(
              'inspectionTypes.subtitle',
              'The kinds of inspection your team carries out. Rename, add and order them; deactivate one to hide it from new inspections.',
            )}
          </p>
        </div>
        {canCreate && (
          <Button size="sm" className="self-start" onClick={() => setDialog({ type: null })}>
            <Plus className="mr-1.5 h-3 w-3" />
            {t('inspectionTypes.add', 'Add type')}
          </Button>
        )}
      </div>

      <DataTable<InspectionSubType>
        tableId="inspection-types"
        columns={columns}
        data={types}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(row) => row.uuid}
        onRowClick={canEdit ? (row) => setDialog({ type: row }) : undefined}
        emptyMessage={t(
          'inspectionTypes.empty',
          'No inspection types yet. Add one to start creating inspections.',
        )}
      />

      <InspectionTypeDialog
        open={dialog !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        type={dialog?.type ?? null}
      />
    </div>
  )
}
